import { hydrate, liveStates } from './storeRegistry'

/**
 * Key the migrated stores travel under inside `window.__INITIAL_STATE__`.
 *
 * They were nested under one reserved key so that, while Vuex still existed, Quasar's client entry
 * could hand the whole payload to `replaceState()` without a migrated store being mistaken for a
 * Vuex module. Vuex is gone; the key stays because the payload shape is a contract between the
 * server render and the hydration on the other side.
 */
export const MIGRATED_STATE_KEY = '__migrated'

/**
 * Called once per request (SSR) or once per page load (SPA) from each app's `boot/ssrState.ts`,
 * which Quasar invokes with the request's `ssrContext`.
 *
 * Server: exposes the migrated stores on `ssrContext.state`, which `@quasar/ssr-helpers` serialises into `window.__INITIAL_STATE__` after
 * the render -- for any app, with a store or without one. A getter rather than an assignment because
 * the serialisation happens AFTER the render while this runs before it, so a snapshot would drop
 * whatever the render itself wrote (which is where `preFetch` puts the page meta).
 *
 * Client: hydrates the migrated stores from the server payload.
 *
 * Quasar 1 asked for a store factory and called `replaceState` on the result, so this used to live
 * inside `src/store/index.ts` and hand back a Vuex-shaped shim. Quasar 2 asks for no store, hence a
 * boot file and no return value.
 *
 * The request's store context is NOT established here: `src-ssr/middlewares/render.ts` wraps the
 * whole render in `runInRequestContext`, which is the only way to scope it correctly (see
 * storeContext). This runs inside that.
 *
 * `vuexStore` is what it says and is always `undefined` now: it merged a Vuex store's state into the
 * same payload while both existed. Kept so the payload's shape is stated in one place.
 */
export function installSsrStateTransport (
  vuexStore: { state: any } | undefined,
  ssrContext: any
): void {
  if (process.env.SERVER) {
    Object.defineProperty(ssrContext, 'state', {
      configurable: true,
      enumerable: true,
      get () {
        return {
          ...(vuexStore ? vuexStore.state : {}),
          [MIGRATED_STATE_KEY]: liveStates()
        }
      }
    })

    return
  }

  if (typeof window !== 'undefined') {
    hydrate((window as any).__INITIAL_STATE__?.[MIGRATED_STATE_KEY])
  }
}
